import { useState, useEffect, useRef, useCallback } from 'react';
import { Plus, X } from 'lucide-react';
import type { Project, Collaborator, GridColumn, StrategicAssessment, SequencingAssessment } from '../types';
import { useT } from '../i18n';

// ── Constants ──

export const VALUE_ADDED_OPTIONS = ['Time', 'Quality', 'Risk', 'Cost', 'Impact', 'Revenue'];
export const EFFORT_OPTIONS = ['Days', 'Weeks', '1–3 months', '+3 months'];
export const TIMELINE_OPTIONS = ['Weeks', '1–3 months', '+3 months'];

// ── Form state ──

// A project deliverable: a card with a short title and an optional longer
// description. `id` is stable (like Milestone.id) so milestones can link to
// a specific deliverable via acceptanceDeliverables regardless of edits to
// its title/body.
export interface Deliverable {
  id: string;
  title: string;
  body: string;
}

export interface MilestoneAssignment { name: string; note: string; }
// One recorded change of a milestone date (start or end) — old/new date plus
// who/when. Computed and appended server-side only (see
// applyMilestoneDateHistory); the client never writes this, just displays it.
export interface MilestoneDueDateChange {
  old_date: string | null;
  new_date: string | null;
  changed_at: string;  // ISO timestamp
  changed_by: string;  // actor name
}
export interface Milestone {
  type?: 'standard' | 'process';
  id: string;                       // stable id for drag-and-drop reordering
  name: string;                     // title
  contents: string;                 // description
  projectedScope: string;           // legacy / optional
  status: string;                   // 'pending' | 'active' | 'completed' (set during Execution) | 'cancelled'
  // Set by the server once cancelled (read-only here). A milestone cancelled in
  // the editor but not saved yet has status 'cancelled' and no record; its
  // former status is kept in statusBeforeCancel so it can be put back.
  cancellation?: { at: string | null; by: string | null; reason: string | null } | null;
  statusBeforeCancel?: string;
  estimatedStart: string;           // 'YYYY-MM-DD' — planned start of implementation
  estimatedEnd: string;             // 'YYYY-MM-DD' — planned end of this milestone
  assignments: MilestoneAssignment[]; // per-collaborator notes
  acceptance: string;               // acceptance criteria (free text)
  acceptanceDeliverables: string[]; // optional links to project deliverables
  dueDateHistory: MilestoneDueDateChange[]; // every past change of estimatedEnd, oldest first
  startDateHistory: MilestoneDueDateChange[]; // every past change of estimatedStart, oldest first
}

export interface FormState {
  name: string;
  problem: string;
  goal: string;
  valueAdded: string[];
  deliverables: Deliverable[];
  effortSize: string;
  timeline: string;
  cash: string;
  collaborators: Collaborator[];
  sponsor: string;
  pmoAccompanist: string;
  userGroups: string[];
  infrastructure: string[];
  statusId: number | null;
  domainId: number | null;
  levelId: number | null;
  // Préqualification stage
  prequalNotes: string;
  prequalContactedSponsor: boolean;
  prequalVerdict: string;            // '' | 'advance' | 'defer'
  // Scoping stage
  scopeIncludes: string;
  scopeExcludes: string;
  urgency: string;                   // '' | 'Low' | 'Medium' | 'High'
  interventionMode: string;          // '' | 'Weekly' | 'Bi-weekly' | 'Monthly'
  timeHorizonStart: string;          // 'YYYY-MM'
  timeHorizonEnd: string;            // 'YYYY-MM'
  totalHours: string;
  // role = free-text "précision"; projectRole = one of the four project roles
  // ([Proposition: roles-projet], see proposals/roles-projet/projectRoles.ts).
  allocations: { name: string; role: string; allocationPct: string; projectRole: string }[];
  cashRequired: boolean;
  cashNotes: string;
  scopeAvailableBudget: string;      // budget confirmed available with finance
  // Planning stage
  checkInCadence: string;
  milestones: Milestone[];
  // Execution stage — PMO-assessed health, set by the operator in PMO meetings
  executionHealth: string;           // '' | 'red' | 'orange' | 'green'
  // Handover stage
  handoverNotes: string;
  // Deferred review state
  deferredReview: string;            // '' | 'to_review' | 'reviewed'
  // Préqualification (RCAJO / AI) — structured assessments stored as JSONB
  rcajo: StrategicAssessment | null;
  sequencing: SequencingAssessment | null;
}

export const EMPTY_FORM: FormState = {
  name: '', problem: '', goal: '',
  valueAdded: [], deliverables: [],
  effortSize: '', timeline: '', cash: '',
  collaborators: [], sponsor: '', pmoAccompanist: '', userGroups: [],
  infrastructure: [],
  statusId: null, domainId: null, levelId: null,
  prequalNotes: '', prequalContactedSponsor: false, prequalVerdict: '',
  scopeIncludes: '', scopeExcludes: '', urgency: '', interventionMode: '',
  timeHorizonStart: '', timeHorizonEnd: '', totalHours: '', allocations: [],
  cashRequired: false, cashNotes: '', scopeAvailableBudget: '',
  checkInCadence: '', milestones: [],
  executionHealth: '',
  handoverNotes: '',
  deferredReview: '',
  rcajo: null,
  sequencing: null,
};

// ── Helpers ──

export function parseJSON<T>(val: any, fallback: T): T {
  if (!val) return fallback;
  if (typeof val === 'object') return val;
  try { return JSON.parse(val); } catch { return fallback; }
}

// 'YYYY-MM-DD' -> 'DD.MM.YY', or '' when unset/malformed.
export function formatMilestoneDueDate(ymd: string | null | undefined): string {
  const [y, m, d] = (ymd || '').split('-');
  return y && m && d ? `${d}.${m}.${y.slice(-2)}` : '';
}

// Milestones live inline in a project's `planning` JSON (not a normalized
// table), keyed by a client-generated id. Used to populate a task's milestone
// dropdown from the project it belongs to. Milestone titles can run long, so
// options are labeled by position + due date instead (e.g. "1 - 15.03.26")
// rather than the milestone name; `noDatePlaceholder` fills in when a
// milestone has no estimated end date yet.
export function getMilestoneOptions(
  planning: string | null | undefined,
  noDatePlaceholder = 'jj.mm.aa',
  dueDateLabel = 'Fin le',
): { value: string; label: string }[] {
  const ms = parseJSON<any>(planning, {}).milestones;
  // A cancelled milestone takes no task, but keeps its number: the others aren't renumbered.
  return Array.isArray(ms)
    ? ms.flatMap((m: any, i: number) => m?.status === 'cancelled' ? [] : [{
        value: m.id,
        label: `${i + 1} - ${dueDateLabel} ${formatMilestoneDueDate(m.estimated_end) || noDatePlaceholder}`,
      }])
    : [];
}

// Map of milestone id → estimated end date ('YYYY-MM-DD' or ''). Lets a task
// clamp its due date to the milestone it is linked to.
export function getMilestoneDueMap(planning: string | null | undefined): Map<string, string> {
  const ms = parseJSON<any>(planning, {}).milestones;
  const map = new Map<string, string>();
  if (Array.isArray(ms)) ms.forEach((m: any) => { if (m?.id) map.set(String(m.id), m.estimated_end || ''); });
  return map;
}

// A milestone-linked task's due date is bounded by the milestone's estimated end:
// adopt it when no due date is set, and pull an existing due date back if it runs
// past the milestone. A milestone with no date leaves the due date untouched.
export function clampDueToMilestone(currentDue: string, milestoneEnd: string): string {
  if (!milestoneEnd) return currentDue;
  if (!currentDue) return milestoneEnd;
  return currentDue > milestoneEnd ? milestoneEnd : currentDue;
}

export function newDeliverable(): Deliverable {
  return { id: 'd' + Math.random().toString(36).slice(2, 9), title: '', body: '' };
}

// Deliverables used to be stored as a flat string[]. Parsed here so legacy
// projects still render: a legacy string becomes its own id (so any existing
// milestone acceptanceDeliverables links — which stored the raw string —
// keep matching) with an empty body.
export function parseDeliverables(val: any): Deliverable[] {
  const arr = parseJSON<any[]>(val, []);
  return arr.map((d, i) => typeof d === 'string'
    ? { id: d, title: d, body: '' }
    : { id: d.id || `d${i}`, title: d.title || '', body: d.body || '' });
}

export function countFilled(form: FormState): number {
  let n = 0;
  if (form.name.trim()) n++;
  if (form.problem.trim()) n++;
  if (form.goal.trim()) n++;
  if (form.valueAdded.length > 0) n++;
  if (form.deliverables.length > 0) n++;
  if (form.effortSize) n++;
  return n;
}

// ── Stage advancement gates ──

// Linear stage order for advancement (side-states Backlog/Deferred excluded).
const ADVANCE_ORDER = ['Inbox', 'Préqualification', 'Scoping', 'Qualification', 'Planning', 'Execution', 'Handover', 'Closed'];

export interface StageGate { nextLabel: string | null; nextStatusId: number | null; missing: string[]; canAdvance: boolean; }

/** Where on the project page a checklist item is filled (see utils/stages ProjectTopic). */
export type ChecklistTopic = 'contenu' | 'equipe' | 'strategie' | 'plan';

/** One requirement for leaving a stage: its label key, whether it is met, and the topic tab that holds it. */
export interface StageChecklistItem { key: string; labelKey: string; ok: boolean; topic: ChecklistTopic; }

/**
 * What a stage needs before the project can move on, item by item (met or
 * not). Qualification, Execution and Handover have no field requirement —
 * they move on a council or manual decision — and return an empty list.
 */
export function getStageChecklist(currentLabel: string, form: FormState): StageChecklistItem[] {
  const items: StageChecklistItem[] = [];
  const item = (key: string, ok: boolean, topic: ChecklistTopic) => items.push({ key, labelKey: `projectForm.gate.${key}`, ok, topic });
  switch (currentLabel) {
    case 'Inbox':
      item('name', !!form.name.trim(), 'contenu');
      item('problem', !!form.problem.trim(), 'contenu');
      item('goal', !!form.goal.trim(), 'contenu');
      item('deliverables', form.deliverables.length > 0, 'contenu');
      item('valueAdded', form.valueAdded.length > 0, 'contenu');
      item('effortEstimate', !!form.effortSize, 'equipe');
      item('sponsor', !!form.sponsor.trim(), 'equipe');
      break;
    case 'Préqualification':
      item('verdictAdvance', form.prequalVerdict === 'advance', 'strategie');
      break;
    case 'Scoping':
      item('inScope', !!form.scopeIncludes.trim(), 'contenu');
      item('outOfScope', !!form.scopeExcludes.trim(), 'contenu');
      item('urgency', !!form.urgency, 'equipe');
      item('interventionMode', !!form.interventionMode, 'equipe');
      item('timeHorizon', !!form.timeHorizonStart && !!form.timeHorizonEnd, 'equipe');
      item('atLeastOneAllocation', form.allocations.length > 0, 'equipe');
      break;
    case 'Planning':
      item('atLeastOneMilestone', form.milestones.some(m => m.status !== 'cancelled'), 'plan');
      break;
    default:
      break;
  }
  return items;
}

// Given the current stage label + form, report what's required to advance to the next linear stage.
// `t` translates the (already-localized) field labels listed in `missing` — it does not affect
// `nextLabel`, which stays a raw DB pipeline-column label (matched against admin-configured data).
export function getStageGate(currentLabel: string, form: FormState, pipelineColumns: GridColumn[], t: (key: string) => string): StageGate {
  const idx = ADVANCE_ORDER.indexOf(currentLabel);
  // Side-states or unknown/terminal: no advancement target.
  if (idx === -1 || idx >= ADVANCE_ORDER.length - 1) return { nextLabel: null, nextStatusId: null, missing: [], canAdvance: false };
  const nextLabel = ADVANCE_ORDER[idx + 1];
  const nextStatusId = pipelineColumns.find(c => c.label === nextLabel)?.id ?? null;
  const missing = getStageChecklist(currentLabel, form).filter(i => !i.ok).map(i => t(i.labelKey));
  return { nextLabel, nextStatusId, missing, canAdvance: missing.length === 0 && nextStatusId != null };
}

function parseUserGroups(own: any): string[] {
  const ug = own.user_group;
  if (!ug) return [];
  if (Array.isArray(ug)) return ug;
  return ug.split(',').map((s: string) => s.trim()).filter(Boolean);
}

function normalizeAllocation(val: any): number {
  if (typeof val === 'number') return val;
  if (typeof val === 'string') return parseInt(val.replace('%', ''), 10) || 0;
  return 0;
}

export function projectToForm(p: Project): FormState {
  const res = parseJSON<any>(p.resources_effort, {});
  const own = parseJSON<any>(p.ownership, {});
  const dep = parseJSON<any>(p.dependencies, {});
  const preq = parseJSON<any>(p.prequalification, {});
  const scop = parseJSON<any>(p.scoping, {});
  const plan = parseJSON<any>(p.planning, {});
  const exec = parseJSON<any>(p.execution, {});
  const hand = parseJSON<any>(p.handover, {});
  const collabs = (res.collaborators || []).map((c: any) => ({
    name: c.name,
    allocation: normalizeAllocation(c.allocation),
  }));
  return {
    name: p.name || '',
    problem: p.problem || '',
    goal: p.goal || '',
    valueAdded: parseJSON<string[]>(p.value_added, []),
    deliverables: parseDeliverables(p.deliverables),
    effortSize: res.effort_size || '',
    timeline: res.timeline || '',
    cash: res.cash || '',
    collaborators: collabs,
    sponsor: own.sponsor || '',
    pmoAccompanist: own.pmo_accompanist || '',
    userGroups: parseUserGroups(own),
    infrastructure: dep.infrastructure || [],
    statusId: p.status_id,
    domainId: p.domain_id ?? null,
    levelId: p.level_id ?? null,
    prequalNotes: preq.notes || '',
    prequalContactedSponsor: !!preq.contacted_sponsor,
    prequalVerdict: preq.verdict || '',
    scopeIncludes: scop.includes || '',
    scopeExcludes: scop.excludes || '',
    urgency: scop.urgency || '',
    interventionMode: scop.intervention_mode || '',
    timeHorizonStart: scop.time_horizon_start || '',
    timeHorizonEnd: scop.time_horizon_end || '',
    totalHours: scop.total_hours || '',
    allocations: Array.isArray(scop.allocations) ? scop.allocations.map((a: any) => ({ name: a.name || '', role: a.role || '', allocationPct: a.allocation_pct || '', projectRole: a.project_role || '' })) : [],
    cashRequired: !!scop.cash_required,
    cashNotes: scop.cash_notes || '',
    scopeAvailableBudget: scop.available_budget || '',
    checkInCadence: plan.check_in_cadence || '',
    milestones: Array.isArray(plan.milestones) ? plan.milestones.map((m: any, i: number) => ({
      id: m.id || `m${i}-${Math.random().toString(36).slice(2, 8)}`,
      type: m.type === 'process' ? 'process' : 'standard',
      name: m.name || '', contents: m.contents || '', projectedScope: m.projected_scope || '',
      status: m.status || 'pending',
      cancellation: m.status === 'cancelled' ? { at: m.cancellation?.at ?? null, by: m.cancellation?.by ?? null, reason: m.cancellation?.reason ?? null } : null,
      estimatedStart: m.estimated_start || '',
      estimatedEnd: m.estimated_end || '',
      assignments: Array.isArray(m.assignments) ? m.assignments.map((a: any) => ({ name: a.name || '', note: a.note || '' })) : [],
      acceptance: m.acceptance || '',
      acceptanceDeliverables: Array.isArray(m.acceptance_deliverables) ? m.acceptance_deliverables : [],
      dueDateHistory: Array.isArray(m.due_date_history) ? m.due_date_history : [],
      startDateHistory: Array.isArray(m.start_date_history) ? m.start_date_history : [],
    })) : [],
    executionHealth: exec.health || '',
    handoverNotes: hand.notes || '',
    deferredReview: p.deferred_review || '',
    rcajo: parseJSON<StrategicAssessment | null>(p.rcajo, null),
    sequencing: parseJSON<SequencingAssessment | null>(p.sequencing, null),
  };
}

export function formToPayload(form: FormState) {
  return {
    name: form.name,
    problem: form.problem || null,
    goal: form.goal || null,
    value_added: form.valueAdded.length ? JSON.stringify(form.valueAdded) : null,
    deliverables: form.deliverables.length ? JSON.stringify(form.deliverables) : null,
    resources_effort: JSON.stringify({
      effort_size: form.effortSize || null,
      timeline: form.timeline || null,
      cash: form.cash || null,
      collaborators: form.collaborators,
    }),
    ownership: JSON.stringify({
      sponsor: form.sponsor || null,
      pmo_accompanist: form.pmoAccompanist || null,
      user_group: form.userGroups.length ? form.userGroups : null,
    }),
    dependencies: JSON.stringify({
      infrastructure: form.infrastructure,
    }),
    status_id: form.statusId,
    domain_id: form.domainId,
    level_id: form.levelId,
    prequalification: JSON.stringify({
      notes: form.prequalNotes || null,
      contacted_sponsor: form.prequalContactedSponsor,
      verdict: form.prequalVerdict || null,
    }),
    scoping: JSON.stringify({
      includes: form.scopeIncludes || null,
      excludes: form.scopeExcludes || null,
      urgency: form.urgency || null,
      intervention_mode: form.interventionMode || null,
      time_horizon_start: form.timeHorizonStart || null,
      time_horizon_end: form.timeHorizonEnd || null,
      total_hours: form.totalHours || null,
      allocations: form.allocations.map(a => ({ name: a.name, role: a.role, allocation_pct: a.allocationPct, project_role: a.projectRole || null })),
      cash_required: !!(form.cashRequired || form.cash),
      cash_notes: form.cashNotes || null,
      available_budget: form.scopeAvailableBudget || null,
    }),
    planning: JSON.stringify({
      check_in_cadence: form.checkInCadence || null,
      milestones: form.milestones.map(m => ({
        id: m.id, type: m.type || 'standard', name: m.name, contents: m.contents, projected_scope: m.projectedScope, status: m.status,
        estimated_start: m.estimatedStart || null,
        estimated_end: m.estimatedEnd || null,
        assignments: (m.assignments || []).map(a => ({ name: a.name, note: a.note })),
        acceptance: m.acceptance || null,
        acceptance_deliverables: m.acceptanceDeliverables || [],
      })),
    }),
    execution: JSON.stringify({
      health: form.executionHealth || null,
    }),
    handover: JSON.stringify({
      notes: form.handoverNotes || null,
    }),
    deferred_review: form.deferredReview || null,
    rcajo: form.rcajo ? JSON.stringify(form.rcajo) : null,
    sequencing: form.sequencing ? JSON.stringify(form.sequencing) : null,
  };
}

// ═══════════════════════════════════════════
// Auto-growing bare textarea
// ═══════════════════════════════════════════

export function BareTextarea({ value, onChange, placeholder, onBlur }: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  onBlur?: () => void;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);

  const resize = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = el.scrollHeight + 'px';
  }, []);

  useEffect(() => { resize(); }, [value, resize]);

  return (
    <textarea
      ref={ref}
      className="form-input--bare"
      value={value}
      onChange={(e) => { onChange(e.target.value); resize(); }}
      onInput={resize}
      onBlur={onBlur}
      placeholder={placeholder}
      rows={1}
    />
  );
}

// ═══════════════════════════════════════════
// Autosuggest input
// ═══════════════════════════════════════════

export function AutosuggestInput({ value, onChange, placeholder, fetchSuggestions, onCreateNew, bare }: {
  value: string;
  onChange: (val: string) => void;
  placeholder: string;
  fetchSuggestions: (q: string) => Promise<{ id: number; name: string }[]>;
  onCreateNew?: (name: string) => Promise<void>;
  bare?: boolean;
}) {
  const { t } = useT();
  const [suggestions, setSuggestions] = useState<{ id: number; name: string }[]>([]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => { setQuery(value); }, [value]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleInput = async (val: string) => {
    setQuery(val);
    onChange(val);
    if (val.trim().length >= 1) {
      const results = await fetchSuggestions(val.trim());
      setSuggestions(results);
      setOpen(true);
    } else {
      setOpen(false);
    }
  };

  const handleSelect = (name: string) => {
    onChange(name);
    setQuery(name);
    setOpen(false);
  };

  const handleCreate = async () => {
    if (!query.trim() || !onCreateNew) return;
    await onCreateNew(query.trim());
    setOpen(false);
  };

  const exactMatch = suggestions.some(s => s.name.toLowerCase() === query.trim().toLowerCase());

  return (
    <div className="autosuggest-wrapper" ref={wrapperRef}>
      <input
        className={bare ? 'form-input--bare' : 'form-input'}
        value={query}
        onChange={(e) => handleInput(e.target.value)}
        onFocus={() => query.trim() && handleInput(query)}
        placeholder={placeholder}
      />
      {open && (suggestions.length > 0 || query.trim()) && (
        <div className="autosuggest-dropdown">
          {suggestions.map((s) => (
            <div key={s.id} className="autosuggest-option" onClick={() => handleSelect(s.name)}>
              {s.name}
            </div>
          ))}
          {onCreateNew && !exactMatch && query.trim() && (
            <div className="autosuggest-option autosuggest-create" onClick={handleCreate}>
              <Plus className="icon-sm" style={{ verticalAlign: 'middle', marginRight: 4 }} />
              {t('projectForm.addQuoted', { q: query.trim() })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════
// Editable bullet list (deliverables)
// ═══════════════════════════════════════════

export function BulletList({ items, onChange, placeholder, maxItems }: {
  items: string[];
  onChange: (items: string[]) => void;
  placeholder: string;
  maxItems?: number;
}) {
  const { t } = useT();
  const refs = useRef<(HTMLInputElement | null)[]>([]);

  const update = (index: number, val: string) => {
    const next = [...items];
    next[index] = val;
    onChange(next);
  };

  const remove = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  const add = () => {
    if (maxItems && items.length >= maxItems) return;
    onChange([...items, '']);
    setTimeout(() => {
      refs.current[items.length]?.focus();
    }, 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (items[index]?.trim()) add();
    }
    if (e.key === 'Backspace' && !items[index] && items.length > 0) {
      e.preventDefault();
      remove(index);
      setTimeout(() => {
        refs.current[Math.max(0, index - 1)]?.focus();
      }, 0);
    }
  };

  return (
    <div>
      <ul className="bullet-list">
        {items.map((item, i) => (
          <li key={i} className="bullet-item">
            <div className="bullet-marker" />
            <input
              ref={(el) => { refs.current[i] = el; }}
              className="bullet-text"
              value={item}
              onChange={(e) => update(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(e, i)}
              placeholder={placeholder}
            />
            <button className="bullet-remove" onClick={() => remove(i)} title={t('projectForm.bullet.remove')}>
              <X className="icon-sm" style={{ width: 14, height: 14 }} />
            </button>
          </li>
        ))}
      </ul>
      {(!maxItems || items.length < maxItems) && (
        <button className="bullet-add" onClick={add} type="button">
          <Plus className="icon-sm" style={{ width: 14, height: 14 }} /> {t('projectForm.bullet.addOutput')}
        </button>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════
// Tag input (user groups, etc.)
// ═══════════════════════════════════════════

export function TagInput({ tags, onChange, placeholder, fetchSuggestions, onCreateNew }: {
  tags: string[];
  onChange: (tags: string[]) => void;
  placeholder: string;
  fetchSuggestions?: (q: string) => Promise<{ id: number; name: string }[]>;
  onCreateNew?: (name: string) => Promise<void>;
}) {
  const [input, setInput] = useState('');
  const [suggestions, setSuggestions] = useState<{ id: number; name: string }[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setShowSuggestions(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const addTag = (tag: string) => {
    const t = tag.trim();
    if (!t || tags.includes(t)) return;
    onChange([...tags, t]);
    setInput('');
    setShowSuggestions(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (input.trim()) {
        addTag(input);
        if (onCreateNew) onCreateNew(input.trim());
      }
    }
    if (e.key === 'Backspace' && !input && tags.length > 0) {
      onChange(tags.slice(0, -1));
    }
  };

  const handleInput = async (val: string) => {
    setInput(val);
    if (fetchSuggestions && val.trim().length >= 1) {
      const results = await fetchSuggestions(val.trim());
      setSuggestions(results.filter(r => !tags.includes(r.name)));
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <div className="tag-input-container" onClick={() => inputRef.current?.focus()}>
        {tags.map((tag, i) => (
          <span key={i} className="tag-item">
            {tag}
            <button className="tag-remove" onClick={(e) => { e.stopPropagation(); onChange(tags.filter((_, j) => j !== i)); }}>
              <X className="icon-sm" style={{ width: 10, height: 10 }} />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          className="tag-input-field"
          value={input}
          onChange={(e) => handleInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={tags.length === 0 ? placeholder : ''}
        />
      </div>
      {showSuggestions && suggestions.length > 0 && (
        <div className="autosuggest-dropdown">
          {suggestions.map((s) => (
            <div key={s.id} className="autosuggest-option" onClick={() => addTag(s.name)}>
              {s.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════
// Lookup list selector (infrastructure, etc.)
// ═══════════════════════════════════════════

export function LookupListSelect({ items, onChange, placeholder, fetchSuggestions, onCreateNew }: {
  items: string[];
  onChange: (items: string[]) => void;
  placeholder: string;
  fetchSuggestions: (q: string) => Promise<{ id: number; name: string }[]>;
  onCreateNew?: (name: string) => Promise<void>;
}) {
  const { t } = useT();
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setSearchOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const [suggestions, setSuggestions] = useState<{ id: number; name: string }[]>([]);

  const handleSearch = async (val: string) => {
    setSearch(val);
    if (val.trim().length >= 1) {
      const results = await fetchSuggestions(val.trim());
      setSuggestions(results.filter(r => !items.includes(r.name)));
      setSearchOpen(true);
    } else {
      setSearchOpen(false);
    }
  };

  const addItem = (name: string) => {
    if (!items.includes(name)) onChange([...items, name]);
    setSearch('');
    setSearchOpen(false);
  };

  const handleCreate = async () => {
    if (!search.trim() || !onCreateNew) return;
    await onCreateNew(search.trim());
    addItem(search.trim());
  };

  const exactMatch = suggestions.some(s => s.name.toLowerCase() === search.trim().toLowerCase());

  return (
    <div className="flex flex-col gap-4">
      {items.map((item, i) => (
        <div key={i} className="project-select-item">
          <span>{item}</span>
          <button className="tag-remove" onClick={() => onChange(items.filter((_, j) => j !== i))}>
            <X className="icon-sm" style={{ width: 12, height: 12 }} />
          </button>
        </div>
      ))}
      <div ref={wrapperRef} style={{ position: 'relative' }}>
        <input
          className="form-input--bare"
          placeholder={placeholder}
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
          onFocus={() => search.trim() && handleSearch(search)}
        />
        {searchOpen && (suggestions.length > 0 || search.trim()) && (
          <div className="autosuggest-dropdown">
            {suggestions.map((s) => (
              <div key={s.id} className="autosuggest-option" onClick={() => addItem(s.name)}>
                {s.name}
              </div>
            ))}
            {onCreateNew && !exactMatch && search.trim() && (
              <div className="autosuggest-option autosuggest-create" onClick={handleCreate}>
                <Plus className="icon-sm" style={{ verticalAlign: 'middle', marginRight: 4 }} />
                {t('projectForm.addQuoted', { q: search.trim() })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════
// Collaborators section
// ═══════════════════════════════════════════

export function CollaboratorsSection({ form, setForm, canCreate, api }: {
  form: FormState;
  setForm: (f: FormState) => void;
  canCreate: boolean;
  api: any;
}) {
  const { t } = useT();
  const [search, setSearch] = useState('');
  const [suggestions, setSuggestions] = useState<{ id: number; name: string }[]>([]);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const existing = form.collaborators.map(c => c.name.toLowerCase());

  const handleSearch = async (val: string) => {
    setSearch(val);
    if (val.trim().length >= 1) {
      const results = await api.getPeople(val.trim()) as { id: number; name: string }[];
      setSuggestions(results.filter(r => !existing.includes(r.name.toLowerCase())));
      setOpen(true);
    } else {
      setOpen(false);
    }
  };

  // Insert a collaborator (name only), clear the search, and keep focus so the
  // input acts as a fresh empty line ready for the next person.
  const addCollaborator = (name: string) => {
    const n = name.trim();
    setSearch('');
    setSuggestions([]);
    setOpen(false);
    if (!n || existing.includes(n.toLowerCase())) return;
    setForm({ ...form, collaborators: [...form.collaborators, { name: n, allocation: 0 }] });
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleCreate = async () => {
    const n = search.trim();
    if (!n) return;
    if (canCreate) { try { await api.createPerson(n); } catch { /* non-blocking */ } }
    addCollaborator(n);
  };

  const removeCollaborator = (i: number) => {
    setForm({ ...form, collaborators: form.collaborators.filter((_, j) => j !== i) });
  };

  const exactMatch = suggestions.some(s => s.name.toLowerCase() === search.trim().toLowerCase());

  return (
    <div className="form-group">
      <label className="form-label">{t('projectForm.collaborators.label')}</label>
      <div className="flex flex-col gap-4">
        {form.collaborators.map((c, i) => (
          <div key={i} className="project-select-item">
            <span>{c.name}</span>
            <button className="tag-remove" onClick={() => removeCollaborator(i)} title={t('common.remove')}>
              <X className="icon-sm" style={{ width: 12, height: 12 }} />
            </button>
          </div>
        ))}
        <div ref={wrapperRef} style={{ position: 'relative' }}>
          <input
            ref={inputRef}
            className="form-input--bare"
            placeholder={t('projectForm.collaborators.searchPlaceholder')}
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            onFocus={() => search.trim() && handleSearch(search)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                const exact = suggestions.find(s => s.name.toLowerCase() === search.trim().toLowerCase());
                if (exact) addCollaborator(exact.name);
                else if (canCreate && search.trim()) handleCreate();
                else if (suggestions[0]) addCollaborator(suggestions[0].name);
              }
            }}
          />
          {open && (suggestions.length > 0 || search.trim()) && (
            <div className="autosuggest-dropdown">
              {suggestions.map((s) => (
                <div key={s.id} className="autosuggest-option" onClick={() => addCollaborator(s.name)}>
                  {s.name}
                </div>
              ))}
              {canCreate && !exactMatch && search.trim() && (
                <div className="autosuggest-option autosuggest-create" onClick={handleCreate}>
                  <Plus className="icon-sm" style={{ verticalAlign: 'middle', marginRight: 4 }} />
                  {t('projectForm.addQuoted', { q: search.trim() })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
